
import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { Store } from '@ngrx/store';
import { ContentApiService } from '@core/services/api/content-api.service';
import { combineLatest, Observable, throwError, timer } from 'rxjs';
import { mergeMap, retryWhen, tap } from 'rxjs/operators';
import { NativeClientService } from '@shared/services/native-client.service';
import { UserApiService } from '../services/api/user-api.service';
import { AuthService } from '../services/auth.service';
import { SnackBarService } from '../services/snackbar.service';
import { selectBffBaseUrl } from '../store/selectors/api-config.selectors';
import { selectIsLogoutBy401Error } from '../store/selectors/app-config.selectors';
import { SKIP_ERROR_HANDLER } from '@core/interceptors/skip-error-handler';

import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';

//Interceptor class to adapt error handling

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  // List of urls which errors are expected should be ignored for the key error code
  blackListByErrorCodes: {
    [key: number]: Array<string | ((err: HttpErrorResponse) => boolean)>;
  } = {};

  blockFetchNewToken = false;

  constructor(
    private authService: AuthService,
    private sb: SnackBarService,
    private nativeClientService: NativeClientService,
    private userApiService: UserApiService,
    private contentApiService: ContentApiService,
    private store: Store,
    @Inject(DOCUMENT) private document: Document,
  ) {
    combineLatest([
      this.store.pipe(selectBffBaseUrl),
      this.userApiService.userProfileUrl$,
      this.contentApiService.translationsUrl$,
    ]).subscribe(([url, userProfileUrl, translationsUrl]) => {
      this.blackListByErrorCodes = {
        404: [
          userProfileUrl,
          translationsUrl,
          '/assets/i18n',
          `${url}/app/user/forum`,
          `${url}/app/account/myaccount/identity`,
          (err) => err.url.startsWith(`${url}/partnerplz/`),
        ],
        400: [
          `${url}/app/account/verification`,
          `${url}/identificationRequest/verify`,
        ],
        401: [`${url}/app/user/info`, `${url}/app/account/myaccount/locale`],
        500: [`${url}/app/account/myaccount/messaging/matrix/personflags`],
        409: [(err) => err.url.startsWith(`${url}/app/user/token/has-role/`)],
      };
    });
  }

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (request.context.get(SKIP_ERROR_HANDLER)) {
      return next.handle(request);
    }
    if (this.nativeClientService.isNative) {
      return next.handle(request).pipe(
        tap(
          (_) => {
            // This is intentional
          },
          (error) => {
            const respError = error as HttpErrorResponse;
            const token = request.headers.get('X-CSRF-TOKEN');
            if (respError) this.handleError(error, request.url, token);
          },
        ),
        retryWhen(
          this.genericRetryStrategy({
            excludedStatusCodes: [500, 400, 403, 404, 504, 200],
          }),
        ),
      );
    } else {
      return next.handle(request).pipe(
        tap(
          (_) => {
            // This is intentional
          },
          (error) => {
            const respError = error as HttpErrorResponse;
            const token = request.headers.get('X-CSRF-TOKEN');
            if (respError) this.handleError(error, request.url, token);
          },
        ),
      );
    }
  }

  private handleError(
    err: HttpErrorResponse,
    url: string,
    token: string | null,
  ): void {
    if (this.shouldErrorBeIgnored(err, url)) {
      return;
    }
    if (err.status?.toString().startsWith('5')) {
      this.sb.error('app.errors.500');
    } else {
      switch (err.status) {
        case 400: {
          this.sb.error('app.errors.400');
          break;
        }
        case 401: {
          if (this.nativeClientService.isNative) {
            if (!this.blockFetchNewToken) {
              this.nativeClientService.fetchNewToken();
              this.blockFetchNewToken = true;
              setTimeout(() => {
                this.blockFetchNewToken = false;
              }, 30000);
            }
          } else {
            this.store
              .pipe(selectIsLogoutBy401Error)
              .subscribe((isLogout: boolean) => {
                if (
                  isLogout &&
                  token &&
                  token === this.authService.getToken()
                ) {
                  this.authService.hiddenLogout().subscribe();
                }
              });
            // this.sb.error('app.errors.401', 'X');
          }

          break;
        }
        case 403: {
          if (this.nativeClientService.isNative && !err?.error?.useBffLogin) {
            console.log('403 error app, not bff login');
          } else if (
            this.nativeClientService.isNative &&
            err?.error?.useBffLogin
          ) {
            //do nothing
            this.nativeClientService.fetchNewToken();
          } else if (url.includes('/verification')) {
            this.sb.error('app.errors.invalidVerifyRights');

            // A workaround to logout on identification flow, when token is expired
          } else if (url.includes('identity')) {
            this.sb.error('app.errors.403');
            this.authService.logoutOAuth();
          } else {
            // this.sb.error('app.errors.403');
          }

          // this.authService.logoutOAuth(); // TODO Think about add this again
          break;
        }
        case 404: {
          this.sb.error('app.errors.404');
          break;
        }
        case 412: {
          this.sb.error('app.errors.412');
          break;
        }
        case 202: {
          // For "Remove Facility"-Response in Profile (always sends 202-error message, even if request was successfull -> leave that until backend fixes the error-handling);
          console.log('202 Error');
          break;
        }
        case 500: {
          this.sb.error('app.errors.default');
          break;
        }
        default: {
          if (!this.nativeClientService.isNative) {
            this.sb.error('app.errors.default');
          }
          break;
        }
      }
    }
  }

  genericRetryStrategy =
    ({
      maxRetryAttempts = 2,
      scalingDuration = 1000,
      excludedStatusCodes = [],
    }: {
      maxRetryAttempts?: number;
      scalingDuration?: number;
      excludedStatusCodes?: number[];
    } = {}) =>
    (attempts: Observable<any>) => {
      return attempts.pipe(
        mergeMap((error, i) => {
          const retryAttempt = i + 1;
          // if maximum number of retries have been met
          // or response is a status code we don't wish to retry, throw error
          if (
            retryAttempt > maxRetryAttempts ||
            excludedStatusCodes.find((e) => e === error.status)
          ) {
            return throwError(error);
          }
          // retry after 1s, 2s, etc...
          return timer(retryAttempt * scalingDuration);
        }),
      );
    };

  private shouldErrorBeIgnored(err: HttpErrorResponse, url: string): boolean {
    const errorStatus: number = err.status;
    const urlBlackList = this.blackListByErrorCodes[errorStatus];
    return (
      Array.isArray(urlBlackList) &&
      urlBlackList.some((blackListOption) => {
        const shouldBeIgnored =
          typeof blackListOption === 'function'
            ? blackListOption(err)
            : url.includes(blackListOption);

        return shouldBeIgnored;
      })
    );
  }
}
